/* Instance-scoped lifecycle: 3D loads only after a visitor requests it. */
if (!customElements.get('hente-gacha-display')) customElements.define('hente-gacha-display', class extends HTMLElement {
  connectedCallback() {
    if (this.cleanup) return;
    const status = this.querySelector('[data-status]');
    const mount = this.querySelector('[data-viewer]');
    const launch = this.querySelector('[data-launch]');
    const close = this.querySelector('[data-close]');
    const standalone = this.querySelector('[data-standalone]');
    const cover = this.querySelector('[data-cover]');
    const download = this.querySelector('[data-export]');
    let url, config;
    try {
      url = new URL(this.dataset.viewerUrl);
      if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash || url.pathname !== '/embed') throw new Error('Invalid viewer');
      if (url.origin === location.origin || url.hostname === 'localhost' || url.hostname.endsWith('.localhost') || url.hostname.includes(':') || /^\d+(?:\.\d+){3}$/.test(url.hostname)) throw new Error('Independent HTTPS host required');
      config = JSON.parse(this.querySelector('[data-config]').value);
      const ids = config.machines.map(item => item.venueId + ':' + item.machineId);
      if (new Set(ids).size !== ids.length) throw new Error('Duplicate machine configuration');
    } catch {
      status.textContent = 'The studio is being prepared. You can still explore all collections below.';
      launch.disabled = true;
      return;
    }
    launch.disabled = false; // Configuration validated; the poster is now interactive.
    standalone.href = url.href;
    standalone.hidden = false;
    let frame = null, timer = null, attempts = 0, channel = null;
    const stopTimer = () => { clearInterval(timer); timer = null; };
    const unload = () => {
      stopTimer(); frame?.remove(); frame = null; channel = null;
      cover.hidden = false; close.hidden = true; launch.disabled = false;
      launch.setAttribute('aria-expanded', 'false');
      status.textContent = 'Interactive preview · no purchase required.';
    };
    const send = () => {
      if (++attempts > 90) {
        stopTimer();
        status.textContent = 'The embedded studio could not connect. Try opening it in a new tab below, or close and retry.';
        return;
      }
      frame?.contentWindow?.postMessage({ type: 'hente:display-config', channel, config }, url.origin);
    };
    const receive = event => {
      if (!frame || event.origin !== url.origin || event.source !== frame.contentWindow || event.data?.type !== 'hente:display-applied' || event.data.channel !== channel) return;
      stopTimer();
      const media = event.data.media;
      const valid = media && ['expected', 'loaded', 'failed'].every(key => Number.isInteger(media[key]) && media[key] >= 0 && media[key] <= 8) && media.loaded + media.failed === media.expected;
      status.textContent = valid && media.expected > 0
        ? `Studio ready · ${media.loaded} of ${media.expected} posters loaded${media.failed ? ` · ${media.failed} image unavailable; original cards shown` : ''}. Preview only, not a purchased result.`
        : 'Studio connected · preview only, not a purchased result.';
    };
    const start = () => {
      if (frame) return;
      frame = document.createElement('iframe'); channel = crypto.randomUUID();
      const openedFrame = frame;
      frame.title = 'HeNTeBeNa interactive capsule toy studio';
      frame.referrerPolicy = 'no-referrer';
      frame.setAttribute('sandbox', 'allow-scripts allow-same-origin');
      // Chrome 142+ can classify proxy-assigned ULA/fake-IP addresses as local.
      // Delegate only to this already-validated iframe origin so it can request access instead of hard-blocking.
      frame.setAttribute('allow', 'local-network-access; local-network; loopback-network');
      frame.addEventListener('load', () => {
        if (frame !== openedFrame) return;
        stopTimer(); attempts = 0; send(); timer = setInterval(send, 500);
      });
      frame.addEventListener('error', () => {
        if (frame !== openedFrame) return;
        stopTimer(); status.textContent = 'Unable to open the studio here. Use the new-tab link below.';
      });
      mount.replaceChildren(frame); frame.src = url.href;
      cover.hidden = true; close.hidden = false; launch.disabled = true;
      launch.setAttribute('aria-expanded', 'true');
      status.textContent = 'Opening your little world… This may take a moment on mobile.';
      // Only the new poster opts in. Its collapse must not anchor users back inside the film.
      if (this.hasAttribute('data-focus-on-open')) {
        close.focus({ preventScroll: true });
        this.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    };
    const exportConfig = () => {
      const href = URL.createObjectURL(new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' }));
      const anchor = document.createElement('a'); anchor.href = href; anchor.download = 'hente-display-config.json'; anchor.click();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    };
    const dismiss = () => { unload(); launch.focus(); };
    launch.addEventListener('click', start); close.addEventListener('click', dismiss);
    window.addEventListener('message', receive); download?.addEventListener('click', exportConfig);
    this.cleanup = () => {
      unload(); window.removeEventListener('message', receive);
      launch.removeEventListener('click', start); close.removeEventListener('click', dismiss);
      download?.removeEventListener('click', exportConfig);
    };
  }
  disconnectedCallback() { this.cleanup?.(); this.cleanup = null; }
});
